import { HttpMethod } from "./httpStatus";

/** Ein Mechanismus für API-Sperren; Scope und Auslöser liefert der Eigentümer. */
export const APPLICATION_API_SCOPE = "application";
const ABORT_ERROR_NAME = "AbortError";
const API_V1_PREFIX = "/api/v1/";
// Legacy-Pfade ohne Version, auf die der v1-Pfad vor dem Vergleich abgebildet wird.
const AUTH_ME_PATH = "/api/auth/me";
const AUTH_TENANT_MEMBERSHIPS_PATH = "/api/auth/tenant-memberships";
const JOBS_PATH = "/api/jobs";
const scopes = new Map<string, { blocked: boolean; reads: AbortController; interrupted: boolean; readsIssued: boolean }>();

function scope(key: string) {
  let state = scopes.get(key);
  if (!state) {
    state = { blocked: false, reads: new AbortController(), interrupted: false, readsIssued: false };
    scopes.set(key, state);
  }
  return state;
}

export const apiRequestAborted = () => new DOMException("API requests paused", ABORT_ERROR_NAME);
export const isApiRequestAborted = (error: unknown): boolean =>
  !!error && typeof error === "object" && "name" in error && error.name === ABORT_ERROR_NAME;

export function setApiRequestsBlocked(key: string, blocked: boolean) {
  const state = scope(key);
  if (state.blocked === blocked) return;
  state.blocked = blocked;
  if (blocked) {
    state.interrupted ||= state.readsIssued;
    state.readsIssued = false;
    state.reads.abort(apiRequestAborted());
  } else state.reads = new AbortController();
}

export function consumeApiRequestInterruption(key: string): boolean {
  const state = scope(key);
  const interrupted = state.interrupted;
  state.interrupted = false;
  state.readsIssued = false;
  return interrupted;
}

/** Schreibaktionen nur vor dem Senden sperren, niemals nachträglich abbrechen. */
export function apiRequestSignal(key: string, method = "GET", signal?: AbortSignal | null): AbortSignal | undefined {
  const state = scope(key);
  if (state.blocked) { state.interrupted = true; throw apiRequestAborted(); }
  const signals = signal ? [signal] : [];
  if (["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase())) {
    state.readsIssued = true;
    signals.push(state.reads.signal);
  }
  const combined = signals.length ? AbortSignal.any(signals) : undefined;
  combined?.throwIfAborted();
  return combined;
}

/** Gemeinsame Sitzungs-/Fortschrittswege, keine pauschale Ausnahme für alle GETs. */
export function isApiControlRequest(url: string, method: string): boolean {
  if (method.toUpperCase() !== HttpMethod.Get) return false;
  const pathname = new URL(url, "http://api.invalid").pathname;
  const path = pathname.startsWith(API_V1_PREFIX) ? `/api${pathname.slice(API_V1_PREFIX.length - 1)}` : pathname;
  return path === AUTH_ME_PATH || path.startsWith("/api/auth/me/")
    || path === AUTH_TENANT_MEMBERSHIPS_PATH
    || path === JOBS_PATH || path.startsWith("/api/jobs/")
    || path.startsWith("/api/system/startup-status");
}

export function waitForApiRetry(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(signal.reason); return; }
    const onAbort = () => { clearTimeout(timer); reject(signal?.reason); };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}
